import { SITE_URL, SITE_NAME } from "../components/Seo/Seo";

// openingTimes come back as { day: "MON", times: [{ startTime, endTime }] },
// where each time is an ["HH", "MM"] pair. schema.org wants full day names and
// "HH:MM" strings.
const SCHEMA_DAYS = {
  SUN: "Sunday",
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
};

const pad = (t) => {
  if (!Array.isArray(t) || t.length < 2) return null;
  const h = Number(t[0]);
  const m = Number(t[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// A restaurant with no hours set comes back with openingTimes: null, which a
// default parameter does not catch — guard both levels explicitly.
export function openingHoursSpecification(openingTimes) {
  const spec = [];
  if (!Array.isArray(openingTimes)) return spec;
  openingTimes.forEach((entry) => {
    const dayOfWeek = SCHEMA_DAYS[entry?.day];
    if (!dayOfWeek || !Array.isArray(entry.times)) return;
    entry.times.forEach((t) => {
      const opens = pad(t?.startTime);
      const closes = pad(t?.endTime);
      if (!opens || !closes) return;
      spec.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek,
        opens,
        closes,
      });
    });
  });
  return spec;
}

/**
 * Site-level identity, emitted on the homepage. Gives Google an explicit entity
 * to attach the knowledge panel and sitelinks to instead of inferring one.
 */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    description:
      "RushMyFood is an online food ordering and delivery service connecting customers with local restaurants.",
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "info@rushmyfood.com",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  };
}

/** Enables the sitelinks search box for brand queries. */
export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/restaurant-list?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Per-restaurant schema — the piece that can earn rating stars and hours in the
 * SERP for "<restaurant> Salem delivery" style queries.
 */
export function restaurantSchema({
  name,
  image,
  slug,
  address,
  cuisines,
  openingTimes,
  reviewAvg,
  reviewCount,
  coordinates,
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name,
    url: `${SITE_URL}/restaurant/${slug}`,
    servesCuisine: cuisines?.length ? cuisines : undefined,
    image: image || `${SITE_URL}/logo512.png`,
    priceRange: "$$",
  };

  // `address` arrives as a single free-text string, so it goes in wholesale.
  // Splitting it into locality/region would mean guessing, and a wrong
  // addressLocality is worse for local ranking than none at all.
  if (address) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: address,
      addressCountry: "US",
    };
  }

  // GraphQL returns GeoJSON order — [longitude, latitude].
  if (Array.isArray(coordinates) && coordinates.length === 2) {
    const [lng, lat] = coordinates;
    if (Number(lat) && Number(lng)) {
      schema.geo = {
        "@type": "GeoCoordinates",
        latitude: Number(lat),
        longitude: Number(lng),
      };
    }
  }

  const hours = openingHoursSpecification(openingTimes);
  if (hours.length) schema.openingHoursSpecification = hours;

  // Google rejects AggregateRating with a zero count, and a fabricated rating is
  // a manual-action risk — only emit when there are real ratings.
  if (reviewCount > 0 && reviewAvg > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(reviewAvg).toFixed(1),
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return schema;
}

export function breadcrumbSchema(trail = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}
